import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { PropertyUploadNavigationComponent } from '../property-upload-navigation/property-upload-navigation.component';
import { FloorType, PropertyType } from 'src/app/services/portal-api.service';
import {
  FloorTypeOptions,
  ApartmentOptionsComponent,
  ApartmentOptionButton
} from '../appartment-options/apartment-options.component';
import { ProgressStepperComponent } from 'src/app/shared/components/progress-stepper/progress-stepper.component';
import { PropertyTypeButtonsComponent } from 'src/app/shared/components/property-type-buttons/property-type-buttons.component';
import { RouterModule } from '@angular/router';
import { SellerPropertyTypeButtonsComponent } from 'src/app/shared/components/seller-property-type-buttons/seller-property-type-buttons.component';
import { LanguageRouterLinkDirective } from 'src/app/shared/directives/language-router-link.directive';
import { NumericOnlyDirective } from 'src/app/shared/directives/number-only.directive';
import { TooltipDirective } from 'src/app/shared/directives/tooltip.directive';
import { IFloorDropdownItem, FloorDropdownItemKey, FloorDropdownComponent } from 'src/app/shared/components/floor-dropdown/floor-dropdown.component';

export interface StepOneData {
  data: {
    propertyFloorType?: FloorTypeOptions;
    squaredMeters: string;
    constructionYear: string;
    propertyType: PropertyType;
    floor?: number | undefined;
    floorType?: FloorType | undefined;
    numberOfFloors?: number | undefined;
    numberOfFloorsFloorType?: FloorType | undefined;
  };
  nextStep: string;
  valid: boolean;
}

@Component({
  selector: 'app-step-one',
  templateUrl: './step-one.component.html',
  standalone: true,
  styleUrl: './step-one.component.css',
  imports: [
    CommonModule,
    TooltipDirective,
    SellerPropertyTypeButtonsComponent,
    TranslocoDirective,
    PropertyUploadNavigationComponent,
    ProgressStepperComponent,
    PropertyTypeButtonsComponent,
    ApartmentOptionsComponent,
    RouterModule,
    LanguageRouterLinkDirective,
    NumericOnlyDirective,
    FloorDropdownComponent
  ],
})
export class StepOneComponent {
  @Input() Steps: string[] = [];
  @Input() set data(value: any) {
    if (value) {
      this.selectedPropertyType = value.PropertyType;
      this.propertyArea = value.PropertyArea;
      this.constructionYear = value.constructionYear;
      this.floor = value.floor;
      this.floorType = value.floorType;
      this.numberOfFloors = value.numberOfFloors;
      this.numberOfFloorsFloorType = value.numberOfFloorsFloorType;
    }
  }

  @Output() saveStepOne = new EventEmitter<StepOneData>();
  @ViewChild('year') yearInput!: ElementRef<HTMLInputElement>;

  selectedPropertyType: PropertyType | undefined;
  propertyTypes = PropertyType;
  propertyArea: string | null = null;
  constructionYear: string | undefined = undefined;
  currentYear: number = new Date().getFullYear();
  //selectedFloorType: FloorTypeOptions | undefined;
  apartmentTypes = FloorTypeOptions;
  minConstructionYear = 1800;
  floor?: number | undefined;
  floorType?: FloorType | undefined;
  numberOfFloors?: number | undefined;
  numberOfFloorsFloorType?: FloorType | undefined;

  // basic floor dropdown options
  public basicFloorDropdownOptions: IFloorDropdownItem[] = [
    {
      key: FloorDropdownItemKey.Basement,
      floor: -1,
      floorType: FloorType.Basement,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.SemiBasement,
      floor: -1,
      floorType: FloorType.SemiBasement,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.GroundFloor,
      floor: 0,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.FirstFloor,
      floor: 1,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.SecondFloor,
      floor: 2,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.ThirdFloor,
      floor: 3,
      disabled: false
    },
    {
      key: FloorDropdownItemKey.FourthPlusFloor,
      floor: 4,
      disabled: false
    }
  ];

  // deep copy objects for this component's dropdowns
  public floorOptions: IFloorDropdownItem[] = JSON.parse(JSON.stringify(this.basicFloorDropdownOptions));
  public numberOfFloorsOptions: IFloorDropdownItem[] = JSON.parse(JSON.stringify(this.basicFloorDropdownOptions));

  public Number = Number;


  propertyTypeSelected(propertyType: PropertyType[]) {
    if (propertyType.length > 0) {
      this.selectedPropertyType = propertyType[0];
    } else {
      this.selectedPropertyType = undefined;
    }

    // reset floor related properties on property type change
    this.floor = undefined;
    this.floorType = undefined;
    this.numberOfFloors = undefined;
    this.numberOfFloorsFloorType = undefined;
  }

  apartmentFloorTypeSelected(floorType: ApartmentOptionButton) {
    this.floor = floorType.floor;
    this.floorType = floorType.floorType;
  }

  ConstructionYearChanged(input: HTMLInputElement) {
    const yearValue = Number(input.value);

    if (yearValue > this.currentYear) {
      this.constructionYear = this.currentYear.toString();
      input.value = this.constructionYear;
    } else {
      this.constructionYear = input.value;
    }
  }
  AreaChanged(area: string) {
    this.propertyArea = area !== '' ? area : null;
  }

  public onFloorSelected(floorDropdownItem: IFloorDropdownItem) {
    this.floor = floorDropdownItem.floor;
    this.floorType = floorDropdownItem.floorType;

    let currentFloorIndex = this.floorOptions.findIndex((x) => x.key == floorDropdownItem.key);
    if (currentFloorIndex > -1) {
      this.numberOfFloorsOptions.forEach((x, index) => {
        if (index < currentFloorIndex) {
          x.disabled = true;

          if (this.numberOfFloors === x.floor) {
            this.numberOfFloors = undefined;
          }
        } else {
          x.disabled = false;
        }
      })
    }
  }

  public onNumberOfFloorsSelected(numberOfFloorsDropdownItem: IFloorDropdownItem) {
    this.numberOfFloors = numberOfFloorsDropdownItem.floor;
    this.numberOfFloorsFloorType = numberOfFloorsDropdownItem.floorType;

    let currentNumberOfFloorsIndex = this.numberOfFloorsOptions.findIndex((x) => x.key == numberOfFloorsDropdownItem.key);
    if (currentNumberOfFloorsIndex > -1) {
      this.floorOptions.forEach((x, index) => {
        if (index > currentNumberOfFloorsIndex) {
          x.disabled = true;

          if (this.floor === x.floor) {
            this.floor = undefined;
          }
        } else {
          x.disabled = false;
        }
      })
    }
  }

  SaveStep1() {
    this.saveStepOne.emit({
      data: {
        propertyType: this.selectedPropertyType!,
        squaredMeters: this.propertyArea!,
        constructionYear: this.constructionYear!,
        floor: this.floor,
        floorType: this.floorType,
        numberOfFloors: this.numberOfFloors,
        numberOfFloorsFloorType: this.numberOfFloorsFloorType
      },
      nextStep: 'Step2',
      valid: true,
    });
  }
}
